import React, { useState } from "react";
import axios from "axios";
import { Button, Input } from "../../shared/ui";
import { Save, X, Pencil } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Convert ISO → local "HH:mm" string for <input type="time">
const isoToTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  } catch { return ""; }
};
const isoToDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  } catch { return ""; }
};
const combineDateTime = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  // Build a local Date, then send as ISO (UTC) — backend reads timezone-aware
  const [y, m, d] = dateStr.split("-").map(Number);
  const [hh, mm] = timeStr.split(":").map(Number);
  const dt = new Date(y, m - 1, d, hh, mm);
  return dt.toISOString();
};

/**
 * Inline editor for a single time entry on the timesheet daily view.
 * Admin/manager/office-staff can:
 *  - change start / finish time (recomputes hours server-side)
 *  - reassign the job
 *  - tweak notes / work description
 *
 * Writes via PATCH /api/time/entries/{id}; backend auto-syncs the change
 * back into the timesheet snapshot so the page refreshes correctly.
 */
export const TimeEntryEditor = ({ entry, jobs, onSaved, onCancel }) => {
  const [date, setDate] = useState(isoToDate(entry.clock_in));
  const [startTime, setStartTime] = useState(isoToTime(entry.clock_in));
  const [endTime, setEndTime] = useState(isoToTime(entry.clock_out));
  const [jobId, setJobId] = useState(entry.job_id || "");
  const [notes, setNotes] = useState(entry.work_description || entry.notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const entryId = entry.time_entry_id || entry.id;
    if (!entryId) {
      toast.error("Can't save — missing entry id");
      return;
    }
    const payload = {};
    const newCI = combineDateTime(date, startTime);
    const newCO = combineDateTime(date, endTime);
    if (newCI) payload.clock_in = newCI;
    if (newCO) payload.clock_out = newCO;
    if (jobId !== (entry.job_id || "")) payload.job_id = jobId || null;
    if (notes !== (entry.work_description || entry.notes || "")) payload.notes = notes;

    if (!Object.keys(payload).length) {
      toast.info("Nothing to save");
      onCancel?.();
      return;
    }
    setSaving(true);
    try {
      await axios.patch(`${API}/time/entries/${entryId}`, payload);
      toast.success("Entry updated");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save changes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2 p-3 bg-orange-50 border border-orange-200 rounded" data-testid="time-entry-editor">
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="text-[10px] uppercase font-bold text-slate-500">Date</label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-8 text-xs" data-testid="entry-edit-date" />
        </div>
        <div>
          <label className="text-[10px] uppercase font-bold text-slate-500">Start</label>
          <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="h-8 text-xs" data-testid="entry-edit-start" />
        </div>
        <div>
          <label className="text-[10px] uppercase font-bold text-slate-500">Finish</label>
          <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="h-8 text-xs" data-testid="entry-edit-end" />
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase font-bold text-slate-500">Job</label>
        <select
          value={jobId}
          onChange={(e) => setJobId(e.target.value)}
          className="h-9 text-sm w-full bg-white border-2 border-slate-200 focus:border-orange-500 focus:outline-none rounded-lg px-2"
          data-testid="entry-edit-job"
        >
          <option value="">— No job —</option>
          {(jobs || []).map((j) => (
            <option key={j.id} value={j.id}>
              {j.title || j.name} {j.client_name ? `· ${j.client_name}` : ""}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="text-[10px] uppercase font-bold text-slate-500">Notes / Work description</label>
        <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="h-8 text-xs" data-testid="entry-edit-notes" />
      </div>
      <div className="flex gap-2 justify-end pt-1">
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving} data-testid="entry-edit-cancel">
          <X className="w-3.5 h-3.5 mr-1" /> Cancel
        </Button>
        <Button size="sm" variant="secondary" onClick={save} disabled={saving} data-testid="entry-edit-save">
          <Save className="w-3.5 h-3.5 mr-1" /> {saving ? "Saving..." : "Save"}
        </Button>
      </div>
    </div>
  );
};

/**
 * Inline editor for a travel entry — admin/manager/office-staff can amend
 * clock in/out, starting/finishing point, KMs, vehicle. Writes via
 * PATCH /api/travel/entries/{id}.
 */
export const TravelEntryEditor = ({ entry, machines, onSaved, onCancel }) => {
  const [date, setDate] = useState(isoToDate(entry.clock_in));
  const [startTime, setStartTime] = useState(isoToTime(entry.clock_in));
  const [endTime, setEndTime] = useState(isoToTime(entry.clock_out));
  const [startingPoint, setStartingPoint] = useState(entry.starting_point || "");
  const [destination, setDestination] = useState(entry.destination || "");
  const [finishingPoint, setFinishingPoint] = useState(entry.finishing_point || "");
  const [vehicleId, setVehicleId] = useState(entry.vehicle_id || "");
  const [startingKm, setStartingKm] = useState(entry.starting_km ?? "");
  const [endingKm, setEndingKm] = useState(entry.ending_km ?? "");
  const [notes, setNotes] = useState(entry.notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const payload = {};
    const newCI = combineDateTime(date, startTime);
    const newCO = combineDateTime(date, endTime);
    if (newCI && newCI !== entry.clock_in) payload.clock_in = newCI;
    if (newCO && newCO !== entry.clock_out) payload.clock_out = newCO;
    if (startingPoint !== (entry.starting_point || "")) payload.starting_point = startingPoint;
    if (destination !== (entry.destination || "")) payload.destination = destination;
    if (finishingPoint !== (entry.finishing_point || "")) payload.finishing_point = finishingPoint;
    if (vehicleId !== (entry.vehicle_id || "")) payload.vehicle_id = vehicleId || null;
    if (startingKm !== (entry.starting_km ?? "")) payload.starting_km = startingKm === "" ? null : parseFloat(startingKm);
    if (endingKm !== (entry.ending_km ?? "")) payload.ending_km = endingKm === "" ? null : parseFloat(endingKm);
    if (notes !== (entry.notes || "")) payload.notes = notes;

    if (!Object.keys(payload).length) {
      toast.info("Nothing to save");
      onCancel?.();
      return;
    }
    setSaving(true);
    try {
      await axios.patch(`${API}/travel/entries/${entry.id}`, payload);
      toast.success("Travel updated");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save travel changes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2 p-3 bg-blue-50 border border-blue-200 rounded" data-testid="travel-entry-editor">
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="text-[10px] uppercase font-bold text-slate-500">Date</label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-8 text-xs" data-testid="travel-edit-date" />
        </div>
        <div>
          <label className="text-[10px] uppercase font-bold text-slate-500">Clock In</label>
          <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="h-8 text-xs" data-testid="travel-edit-start" />
        </div>
        <div>
          <label className="text-[10px] uppercase font-bold text-slate-500">Clock Out</label>
          <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="h-8 text-xs" data-testid="travel-edit-end" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="text-[10px] uppercase font-bold text-slate-500">From</label>
          <Input value={startingPoint} onChange={(e) => setStartingPoint(e.target.value)} className="h-8 text-xs" data-testid="travel-edit-from" />
        </div>
        <div>
          <label className="text-[10px] uppercase font-bold text-slate-500">To</label>
          <Input value={destination} onChange={(e) => setDestination(e.target.value)} className="h-8 text-xs" data-testid="travel-edit-to" />
        </div>
        <div>
          <label className="text-[10px] uppercase font-bold text-slate-500">Finished At</label>
          <Input value={finishingPoint} onChange={(e) => setFinishingPoint(e.target.value)} className="h-8 text-xs" data-testid="travel-edit-finish" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="text-[10px] uppercase font-bold text-slate-500">Vehicle</label>
          <select
            value={vehicleId}
            onChange={(e) => setVehicleId(e.target.value)}
            className="h-8 text-xs w-full bg-white border-2 border-slate-200 focus:border-orange-500 focus:outline-none rounded-lg px-2"
            data-testid="travel-edit-vehicle"
          >
            <option value="">— Personal —</option>
            {(machines || []).map((m) => (
              <option key={m.id} value={m.id}>{m.name} {m.rego ? `(${m.rego})` : ""}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase font-bold text-slate-500">Start KM</label>
          <Input type="number" value={startingKm} onChange={(e) => setStartingKm(e.target.value)} className="h-8 text-xs" data-testid="travel-edit-start-km" />
        </div>
        <div>
          <label className="text-[10px] uppercase font-bold text-slate-500">End KM</label>
          <Input type="number" value={endingKm} onChange={(e) => setEndingKm(e.target.value)} className="h-8 text-xs" data-testid="travel-edit-end-km" />
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase font-bold text-slate-500">Notes</label>
        <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="h-8 text-xs" data-testid="travel-edit-notes" />
      </div>
      <div className="flex gap-2 justify-end pt-1">
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving} data-testid="travel-edit-cancel">
          <X className="w-3.5 h-3.5 mr-1" /> Cancel
        </Button>
        <Button size="sm" variant="secondary" onClick={save} disabled={saving} data-testid="travel-edit-save">
          <Save className="w-3.5 h-3.5 mr-1" /> {saving ? "Saving..." : "Save"}
        </Button>
      </div>
    </div>
  );
};

export const EditEntryButton = ({ onClick, ...props }) => (
  <button
    type="button"
    onClick={onClick}
    className="text-[11px] font-bold uppercase text-blue-600 hover:text-blue-700 hover:underline px-2 py-1 rounded whitespace-nowrap inline-flex items-center gap-1"
    {...props}
  >
    <Pencil className="w-3 h-3" /> Edit
  </button>
);
